import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { AnimatePresence, motion } from "framer-motion";
import { playChime, primeAudio } from "@/lib/chime";
import { REACTIONS } from "@/lib/guest";
import {
  Play,
  Pause,
  X,
  Volume2,
  VolumeX,
  Camera,
  ChevronLeft,
  ChevronRight,
  Trophy,
} from "lucide-react";

/**
 * Full-screen rotating slideshow — perfect for projecting on party TVs.
 * Auto-advances every 4.5s, blurred backdrop of current photo, chime on new arrivals.
 */
export default function SlideshowPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [interval_, setIntervalMs] = useState(4500);
  const prevIdsRef = useRef(new Set());
  const wrapRef = useRef(null);

  const load = async () => {
    try {
      const [ev, ph] = await Promise.all([
        api.get(`/public/events/${slug}`),
        api.get(`/public/events/${slug}/photos`),
      ]);
      setEvent(ev.data);
      const list = ph.data.slice().reverse(); // oldest first for natural slideshow order
      const newOnes = list.filter((p) => !prevIdsRef.current.has(p.id));
      if (prevIdsRef.current.size > 0 && newOnes.length > 0 && !muted) {
        playChime();
      }
      list.forEach((p) => prevIdsRef.current.add(p.id));
      setPhotos(list);
    } catch {
      /* no-op */
    }
  };

  useEffect(() => {
    load();
    const int = setInterval(load, 5000);
    return () => clearInterval(int);
    // eslint-disable-next-line
  }, [slug, muted]);

  // Auto-advance
  useEffect(() => {
    if (!playing || photos.length < 2) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % photos.length);
    }, interval_);
    return () => clearInterval(t);
  }, [playing, photos.length, interval_]);

  useEffect(() => {
    // clamp index if photos shrink
    if (index >= photos.length && photos.length > 0) setIndex(0);
  }, [photos.length, index]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      wrapRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const cur = photos[index];

  if (!event) {
    return (
      <div className="min-h-[100dvh] bg-black text-white flex items-center justify-center">
        <div className="text-white/50">Carregando slideshow...</div>
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      onClick={() => primeAudio()}
      className="min-h-[100dvh] w-full bg-black text-white relative overflow-hidden select-none"
      data-testid="slideshow-page"
    >
      {/* Blurred backdrop */}
      {cur && (
        <img
          key={`bg-${cur.id}`}
          src={fileUrl(cur.storage_path)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "blur(60px) brightness(0.4) saturate(1.3)", transform: "scale(1.2)" }}
        />
      )}
      <div className="absolute inset-0 bg-black/40" />

      {/* Header */}
      <div className="absolute top-0 inset-x-0 z-20 p-6 flex items-start justify-between">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-white/50">
            Galeria ao vivo
          </span>
          <h1 className="font-display font-black text-2xl sm:text-4xl tracking-tighter mt-1 text-glow-pink">
            {event.name}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="glass rounded-full px-4 py-2 text-sm flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#FF007F]" />
            <span className="font-bold">{photos.length}</span>
            <span className="text-white/50">fotos</span>
          </div>
          <Link
            to={`/e/${slug}/gallery`}
            className="w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors"
            data-testid="exit-slideshow"
            title="Sair"
          >
            <X className="w-5 h-5" />
          </Link>
        </div>
      </div>

      {/* Slide */}
      <div className="relative z-10 min-h-[100dvh] flex items-center justify-center p-16 sm:p-24">
        {photos.length === 0 ? (
          <div className="text-center">
            <Camera className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/50 text-xl">Aguardando as primeiras fotos...</p>
            <p className="text-white/30 text-sm mt-2">
              Compartilhe o QR code com seus convidados!
            </p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={cur?.id}
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.04, y: -20 }}
              transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
              className="relative max-w-[80vmin] max-h-[75vh]"
              data-testid={`slide-${cur?.id}`}
            >
              <img
                src={cur ? fileUrl(cur.storage_path) : ""}
                alt=""
                className="max-w-full max-h-[75vh] rounded-2xl shadow-[0_0_60px_rgba(255,0,127,0.25)] object-contain"
              />
              <div className="mt-6 text-center">
                <div className="text-xs uppercase tracking-[0.3em] text-white/40">
                  Foto de
                </div>
                <div className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-2 text-[#FF007F] text-glow-pink">
                  {cur?.guest_name}
                </div>
                {cur?.caption && (
                  <div
                    className="mt-3 text-white/80 text-base sm:text-lg max-w-2xl mx-auto px-6 whitespace-pre-wrap break-words"
                    data-testid="slide-caption"
                  >
                    “{cur.caption}”
                  </div>
                )}
                {cur?.reactions_total > 0 && (
                  <motion.div
                    key={`rx-${cur.id}-${cur.reactions_total}`}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="mt-5 flex items-center justify-center gap-3 flex-wrap"
                    data-testid="slide-reactions"
                  >
                    {REACTIONS.map((r) =>
                      cur.reactions?.[r.key] ? (
                        <div
                          key={r.key}
                          className="glass rounded-full px-3 py-1.5 flex items-center gap-1.5"
                        >
                          <span className="text-xl">{r.char}</span>
                          <span className="text-white font-bold text-sm">
                            {cur.reactions[r.key]}
                          </span>
                        </div>
                      ) : null,
                    )}
                    <div className="glass rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.15em] text-[#FFD700] font-bold">
                      total {cur.reactions_total}
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 inset-x-0 z-20 p-6 flex items-center justify-center gap-3">
        <div className="glass rounded-full px-4 py-2 flex items-center gap-2">
          <button
            onClick={() => setIndex((i) => (i - 1 + photos.length) % Math.max(1, photos.length))}
            className="p-2 hover:text-[#FF007F] transition-colors"
            data-testid="slide-prev"
            title="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setPlaying((p) => !p)}
            className="p-2 hover:text-[#FF007F] transition-colors"
            data-testid="slide-playpause"
            title={playing ? "Pausar" : "Retomar"}
          >
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setIndex((i) => (i + 1) % Math.max(1, photos.length))}
            className="p-2 hover:text-[#FF007F] transition-colors"
            data-testid="slide-next"
            title="Próxima"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="w-px h-6 bg-white/15 mx-1" />
          <button
            onClick={() => setMuted((m) => !m)}
            className="p-2 hover:text-[#FF007F] transition-colors"
            data-testid="slide-mute"
            title={muted ? "Ativar som" : "Silenciar"}
          >
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <select
            value={interval_}
            onChange={(e) => setIntervalMs(Number(e.target.value))}
            className="bg-transparent text-xs text-white/70 outline-none cursor-pointer"
            data-testid="slide-speed"
          >
            <option className="bg-black" value={2500}>2.5s</option>
            <option className="bg-black" value={4500}>4.5s</option>
            <option className="bg-black" value={7000}>7s</option>
            <option className="bg-black" value={10000}>10s</option>
          </select>
          <button
            onClick={toggleFullscreen}
            className="ml-1 px-3 py-1 rounded-full text-xs bg-[#FF007F] hover:brightness-110 transition-all"
            data-testid="slide-fullscreen"
          >
            Tela cheia
          </button>
        </div>
        <Link
          to={`/e/${slug}/hall`}
          className="glass rounded-full px-4 py-2 flex items-center gap-2 hover:text-[#FFD700] transition-colors"
          data-testid="slide-hall-link"
          title="Hall da Fama"
        >
          <Trophy className="w-4 h-4 text-[#FFD700]" />
          <span className="text-xs uppercase tracking-[0.15em]">Hall</span>
        </Link>
      </div>

      {/* Progress bar */}
      {photos.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 z-10 h-1 bg-white/10">
          <motion.div
            key={`${index}-${playing}`}
            className="h-full bg-[#FF007F]"
            initial={{ width: "0%" }}
            animate={{ width: playing ? "100%" : "0%" }}
            transition={{ duration: playing ? interval_ / 1000 : 0, ease: "linear" }}
          />
        </div>
      )}
    </div>
  );
}
